/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { action, flow, makeObservable, observable } from 'mobx';
import { GlobalStore, UserStore } from '@roc/feature-app-core';
import { ApiResponse } from '@roc/feature-app-core';
import { GENERIC_ERROR_MESSAGE } from '@roc/feature-utils';
import {
  ExtensionsInternalService,
  LoanExtensionSubmitResponse,
} from '../services/extensionsInternalService';
import { LoanStore } from '@roc/feature-loans';

const toIsoDateTime = (value: any) => {
  if (typeof value !== 'string') return value;
  const match = value.match(
    /^(\d{2})\/(\d{2})\/(\d{4})[ T](\d{1,2}:\d{2}:\d{2}(?:\.\d+)?)$/
  );
  return match ? `${match[3]}-${match[1]}-${match[2]}T${match[4]}` : value;
};

interface NportalActionPayload {
  success?: boolean;
  responseMessage?: string;
  loanExtensionId?: string | number;
}

function parseNportalActionPayload(raw: unknown): NportalActionPayload | null {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw as NportalActionPayload;
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw) as NportalActionPayload;
  } catch {
    return null;
  }
}

export interface Extension {
  loanExtensionId: number;
  loanId: number;
  dealName: string;
  lender: string;
  maturityDate: string;
  requestDate: string;
  existingMaturityDate: string;
  newMaturityDate: string;
  completedDate?: string;
  totalExtensionFee: number;
  anoteExtensionFee: number | null;
  status: string;
  statusList?: string[];
  extensionDuration: number;
  extensionNumber: number;
  extensionWarnings: string;
  extensionInsurance: string;
  notes: string;
  assignedTo?: string;
  feePaymentTime?: string;
  insuranceDocName?: string;
  insuranceReviewerId?: number;
  nextDueDate?: string;
  warningMessage?: string;
  shouldSendEmailCompletion?: boolean;
  createdBy?: string;
  creationDate?: string;
  lastUpdatedBy?: string;
  lastUpdateDate?: string;
}

export class ExtensionsStore {
  globalStore: GlobalStore;
  private extensionsInternalService: ExtensionsInternalService;
  private loanStore: LoanStore;
  private userStore: UserStore;
  public extensions: Extension[] = [];
  public isLoading = false;
  public selectedExtension: Extension = null;
  public selectedExtensionId: number = null;

  constructor(globalStore: GlobalStore, loanStore: LoanStore, userStore: UserStore) {
    this.globalStore = globalStore;
    this.extensionsInternalService = new ExtensionsInternalService();
    this.loanStore = loanStore;
    this.userStore = userStore;

    makeObservable(this, {
      extensions: observable,
      isLoading: observable,
      selectedExtension: observable,
      selectedExtensionId: observable,
      fetchExtensionsByLoanId: flow,
      fetchExtensionById: flow,
      refreshSelectedExtension: flow,
      setExtensions: action,
      setIsLoading: action,
      initExtensionInternal: flow,
      setSelectedExtensionId: action,
      markLoanExtensionAsComplete: flow,
      markLoanExtensionAsRetracted: flow,
      markInsuranceReviewComplete: flow,
      requestExtensionAgreement: flow,
    });
  }

  setExtensions(extensions: Extension[]) {
    this.extensions = extensions;
  }

  setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  setSelectedExtension(extension: Extension) {
    this.selectedExtension = extension;
  }

  setSelectedExtensionId(selectedExtensionId: number) {
    this.selectedExtensionId = selectedExtensionId;
  }

  *fetchExtensionsByLoanId(loanId: string) {
    this.setIsLoading(true);
    try {
      const response: ApiResponse = yield this.extensionsInternalService.getLoanExtensionsByLoanId(loanId);
      const { data } = response;
      this.setExtensions(data?.data?.rows || []);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
      this.setExtensions([]);
    } finally {
      this.setIsLoading(false);
    }
  }

  *fetchExtensionById(extensionId: number) {
    if (extensionId == null) return null;
    const response: ApiResponse = yield this.extensionsInternalService.getLoanExtensionById(extensionId);
    const rawInner = response?.data?.data;
    const parsed: Extension | null = typeof rawInner === 'string'
      ? JSON.parse(rawInner)
      : (rawInner ?? null);
    return parsed;
  }

  *refreshSelectedExtension(loanId: string) {
    yield this.fetchExtensionsByLoanId(loanId);
    if (this.selectedExtensionId != null) {
      const current = this.extensions.find(
        ext => ext.loanExtensionId === this.selectedExtensionId
      );
      if (current) {
        this.setSelectedExtension(current);
      }
    }
  }

  *initExtensionInternal(loanId: string) {
    yield this.userStore.getUserInfoAndEntitlements();
    yield this.loanStore.fetchLoanDetails(loanId);
    yield this.fetchExtensionsByLoanId(loanId);
    if (this.extensions?.length > 0) {
      const lastExtension = this.extensions.reduce((max, ext) =>
        ext.extensionNumber > max.extensionNumber ? ext : max
      );
      this.setSelectedExtension(lastExtension);
      this.setSelectedExtensionId(lastExtension.loanExtensionId);
    } else {
      this.setSelectedExtensionId(this.selectedExtension?.loanExtensionId ?? null);
    }
  }

  *markLoanExtensionAsComplete(extensionId: number) {
    try {
      const response = yield this.extensionsInternalService.markLoanExtensionAsComplete(extensionId);
      const innerPayload = parseNportalActionPayload(response?.data?.data);

      if (innerPayload?.success === false) {
        this.globalStore.notificationStore.showErrorNotification({
          message: innerPayload?.responseMessage,
        });
      } else {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Extension marked as complete successfully.',
        });
      }
      yield this.fetchExtensionsByLoanId(this.loanStore.loanDetails.loanId);
      yield this.initExtensionInternal(this.loanStore.loanDetails.loanId);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *markLoanExtensionAsRetracted(extensionId: number) {
    try {
      const response = yield this.extensionsInternalService.markLoanExtensionAsRetracted(extensionId);
      const innerPayload = parseNportalActionPayload(response?.data?.data);

      if (innerPayload?.success === false) {
        this.globalStore.notificationStore.showErrorNotification({
          message: innerPayload?.responseMessage,
        });
      } else {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Extension marked as retracted successfully.',
        });
      }
      yield this.fetchExtensionsByLoanId(this.loanStore.loanDetails.loanId);
      yield this.initExtensionInternal(this.loanStore.loanDetails.loanId);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *markInsuranceReviewComplete() {
    try {
      const freshExtension = yield this.fetchExtensionById(this.selectedExtensionId);

      if (freshExtension) {
        this.setSelectedExtension(freshExtension);
      }

      const source: any = { ...(freshExtension ?? this.selectedExtension) };
      source.lastUpdateDate = toIsoDateTime(source.lastUpdateDate);
      source.creationDate = toIsoDateTime(source.creationDate);
      const payload = {
        ...source,
        status: "Insurance Review Completed"
      };
      const response = yield this.extensionsInternalService.submitLoanExtensionInternal(payload);
      const result: LoanExtensionSubmitResponse | undefined = response?.data?.data;

      if (result?.success === false) {
        this.globalStore.notificationStore.showErrorNotification({
          message: result.responseMessage,
        });
      } else {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Extension status was succesfully updated',
        });
      }

      yield this.fetchExtensionsByLoanId(this.loanStore.loanDetails.loanId);
      yield this.initExtensionInternal(this.loanStore.loanDetails.loanId);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *requestExtensionAgreement() {
    try {

      const freshExtension = yield this.fetchExtensionById(this.selectedExtensionId);

      if (freshExtension) {
        this.setSelectedExtension(freshExtension);
      }

      const payload = freshExtension ?? this.selectedExtension;
      const response = yield this.extensionsInternalService.requestExtensionAgreement(payload);
      const innerPayload = parseNportalActionPayload(response?.data?.data);

      if (innerPayload?.success === false) {
        this.globalStore.notificationStore.showErrorNotification({
          message: innerPayload?.responseMessage,
        });
      } else {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Extension agreement was succesfully requested',
        });
      }

      yield this.fetchExtensionsByLoanId(this.loanStore.loanDetails.loanId);
      yield this.initExtensionInternal(this.loanStore.loanDetails.loanId);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

}
